import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Tag } from "lucide-react";
import { ImageUpload } from "@/components/ImageUpload";

const empty = { name: "", slug: "", description: "", feature_image: "", display_order: 0, is_active: true };

/**
 * CourseCategoriesDialog — admin CRUD for course-categories. Opens from a button
 * in CoursesTab. Single dialog with list + inline editor.
 */
export default function CourseCategoriesDialog({ open, onOpenChange }) {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null); // null | {} (new) | row
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);

    const reload = () => api.get("/admin/course-categories").then(({ data }) => setItems(data));
    useEffect(() => { if (open) reload(); }, [open]);

    const startNew = () => { setEditing({}); setForm(empty); };
    const startEdit = (row) => { setEditing(row); setForm({ ...empty, ...row }); };
    const cancelEdit = () => { setEditing(null); setForm(empty); };

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((f) => ({
            ...f,
            [name]: type === "checkbox" ? checked : (name === "display_order" ? Number(value) : value),
        }));
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form, slug: form.slug?.trim() || undefined };
            if (editing?.id) {
                await api.patch(`/admin/course-categories/${editing.id}`, payload);
                toast.success("Category updated");
            } else {
                await api.post("/admin/course-categories", payload);
                toast.success("Category created");
            }
            cancelEdit();
            reload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    const remove = async (row) => {
        if (!confirm(`Delete "${row.name}"? Courses in it will be uncategorised.`)) return;
        try {
            await api.delete(`/admin/course-categories/${row.id}`);
            toast.success("Category deleted");
            reload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="course-categories-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">Course categories</DialogTitle>
                    <DialogDescription>Group courses for the homepage and the course detail page.</DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {!editing && (
                        <div className="flex justify-end">
                            <Button onClick={startNew} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta" data-testid="course-category-new-btn">
                                <Plus className="h-4 w-4 mr-1.5" /> New category
                            </Button>
                        </div>
                    )}

                    {editing && (
                        <form onSubmit={save} className="bg-paper-alt rounded-2xl p-4 space-y-4" data-testid="course-category-form">
                            <div>
                                <Label>Feature image</Label>
                                <div className="mt-1.5">
                                    <ImageUpload
                                        value={form.feature_image || ""}
                                        onChange={(url) => setForm((f) => ({ ...f, feature_image: url || "" }))}
                                        testIdPrefix="course-category-feature-image"
                                    />
                                </div>
                                <p className="text-[11px] text-ink-400 mt-1">Shown on the homepage category card and the dedicated category page banner.</p>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <Label>Name</Label>
                                    <Input name="name" value={form.name} onChange={onChange} required className="mt-1.5 rounded-xl" data-testid="course-category-name-input" />
                                </div>
                                <div>
                                    <Label>Slug (auto if empty)</Label>
                                    <Input name="slug" value={form.slug || ""} onChange={onChange} placeholder="auto-generated" className="mt-1.5 rounded-xl" />
                                </div>
                                <div>
                                    <Label>Display order</Label>
                                    <Input type="number" name="display_order" value={form.display_order} onChange={onChange} className="mt-1.5 rounded-xl" />
                                </div>
                                <label className="flex items-end gap-2 text-sm pb-2.5">
                                    <input type="checkbox" name="is_active" checked={form.is_active} onChange={onChange} />
                                    Active (visible on storefront)
                                </label>
                            </div>
                            <div>
                                <Label>Description</Label>
                                <Textarea name="description" value={form.description || ""} onChange={onChange} rows={2} className="mt-1.5 rounded-xl" />
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button type="button" variant="outline" onClick={cancelEdit} className="rounded-full">Cancel</Button>
                                <Button type="submit" disabled={saving} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta" data-testid="course-category-save-btn">
                                    {saving ? "Saving…" : editing?.id ? "Save changes" : "Create"}
                                </Button>
                            </div>
                        </form>
                    )}

                    <div className="space-y-2">
                        {items.length === 0 && !editing && (
                            <p className="text-center py-8 text-ink-700 text-sm">No course categories yet — click "New category" above.</p>
                        )}
                        {items.map((c) => (
                            <div key={c.id} className="rounded-2xl bg-white border border-gray-100 p-3.5 flex items-center justify-between" data-testid={`course-category-row-${c.id}`}>
                                <div className="flex items-center gap-3 min-w-0">
                                    {c.feature_image ? (
                                        <img src={c.feature_image} alt={c.name} className="h-10 w-10 rounded-xl object-cover ring-1 ring-gray-200" />
                                    ) : (
                                        <div className="h-10 w-10 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center"><Tag className="h-4 w-4" /></div>
                                    )}
                                    <div className="min-w-0">
                                        <div className="font-semibold truncate">{c.name} {!c.is_active && <span className="text-[10px] uppercase ml-1 bg-gray-100 text-ink-700 rounded-full px-2 py-0.5">hidden</span>}</div>
                                        <div className="text-xs text-ink-700 truncate">/{c.slug} · order {c.display_order}</div>
                                    </div>
                                </div>
                                <div className="flex gap-1.5">
                                    <Button size="icon" variant="outline" className="rounded-full h-8 w-8" onClick={() => startEdit(c)} data-testid={`course-category-edit-${c.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                                    <Button size="icon" variant="outline" className="rounded-full h-8 w-8 text-accent-700 hover:bg-accent-50" onClick={() => remove(c)} data-testid={`course-category-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <DialogFooter>
                    <Button onClick={() => onOpenChange(false)} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta">Done</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
